/**
 * Review decisions — *Keep mine* (spec `observer-autogen-review-walkthrough`, R4.3, R5.3) and
 * *Discard* (spec `observer-autogen-review-discard-and-scores`, R1): the session's copy, and how they
 * ride the plan save.
 *
 * Mirrors the drawn-against records (`observerStamps.ts`): one store holds the session's decisions and
 * what the server holds; every save sends them as the plan's top-level `suggestionDecisions`; a change
 * with nothing else changed asks for a QUIET save — a decision is not an edit, so it never marks the
 * plan as unlaunched work. ⚠ Not under `observerDraft`: that retired marker's mere presence makes
 * `hasUnlaunchedEdits` read the plan as edited (`draftChoice.ts`).
 */
import { create } from 'zustand';
import type {
  FloorPlannerDiscardReason,
  FloorPlannerLayout,
  FloorPlannerSuggestionDecision,
} from '@repo/frontend-types';

export type SuggestionDecisionRecord = FloorPlannerSuggestionDecision;

interface SuggestionDecisionState {
  decisions: SuggestionDecisionRecord[];
  /** The decisions the server holds, as last read or written. */
  saved: SuggestionDecisionRecord[];
  /** Keep mine or Discard: one decision per machine and camera, the newest winning. */
  keep: (decision: SuggestionDecisionRecord) => void;
  /** A discard's optional reason; `null` clears it. Changes nothing for any other decision. */
  setReason: (
    targetRefid: string,
    cameraBid: string,
    reason: FloorPlannerDiscardReason | null,
  ) => void;
  /** *Ask again*: drop the decision for this machine on this camera. */
  forget: (targetRefid: string, cameraBid: string) => void;
  /** Start a session from a saved plan: its decisions are both the session's and what is saved. */
  restore: (decisions: SuggestionDecisionRecord[]) => void;
  /** A save that carried these decisions landed. */
  markSaved: (decisions: SuggestionDecisionRecord[]) => void;
}

const sameKey = (a: SuggestionDecisionRecord, b: SuggestionDecisionRecord) =>
  a.targetRefid === b.targetRefid && a.cameraBid === b.cameraBid;

export const useSuggestionDecisionStore = create<SuggestionDecisionState>()(set => ({
  decisions: [],
  saved: [],
  keep: decision =>
    set(state => ({
      decisions: [...state.decisions.filter(d => !sameKey(d, decision)), decision],
    })),
  setReason: (targetRefid, cameraBid, reason) =>
    set(state => ({
      decisions: state.decisions.map(d => {
        if (d.targetRefid !== targetRefid || d.cameraBid !== cameraBid || d.kind !== 'discard') {
          return d;
        }
        const { reason: _old, ...rest } = d;
        return reason ? { ...rest, reason } : rest;
      }),
    })),
  forget: (targetRefid, cameraBid) =>
    set(state => ({
      decisions: state.decisions.filter(
        d => !(d.targetRefid === targetRefid && d.cameraBid === cameraBid),
      ),
    })),
  restore: decisions => set({ decisions: [...decisions], saved: [...decisions] }),
  markSaved: decisions => set({ saved: [...decisions] }),
}));

const PARTS = new Set(['HEAD', 'TORSO', 'HIPS', 'LEGS', 'FEET']);
const REASONS = new Set<string>(['wrong-place', 'wrong-machine', 'not-a-machine']);
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** The parts of a stored record beyond the four every decision has, validated; null when unreadable. */
const discardFields = (
  r: Partial<SuggestionDecisionRecord>,
): Pick<SuggestionDecisionRecord, 'kind' | 'reason' | 'centroid' | 'size'> | null => {
  if (r.kind === undefined || r.kind === 'keep') return r.kind === 'keep' ? { kind: 'keep' } : {};
  if (r.kind !== 'discard') return null;
  const c = r.centroid;
  if (!c || !finite(c.x) || !finite(c.y) || !finite(r.size) || r.size <= 0) return null;
  if (r.reason !== undefined && !REASONS.has(r.reason)) return null;
  return {
    kind: 'discard',
    centroid: { x: c.x, y: c.y },
    size: r.size,
    ...(r.reason ? { reason: r.reason } : {}),
  };
};

/**
 * A saved plan's decisions, validated. The layout comes off a JSON column, so a record that is not
 * readable is dropped and logged — a bad record never settles a machine on its own.
 */
export const readSuggestionDecisions = (
  layout: FloorPlannerLayout | null | undefined,
): SuggestionDecisionRecord[] => {
  const stored = (layout as { suggestionDecisions?: unknown } | null | undefined)
    ?.suggestionDecisions;
  if (!Array.isArray(stored)) return [];
  const out: SuggestionDecisionRecord[] = [];
  for (const raw of stored) {
    const r = raw as Partial<SuggestionDecisionRecord> | null;
    const extra = r ? discardFields(r) : null;
    if (
      r &&
      extra &&
      typeof r.targetRefid === 'string' &&
      typeof r.cameraBid === 'string' &&
      typeof r.trackSide === 'string' &&
      PARTS.has(r.trackSide) &&
      typeof r.decidedAt === 'string'
    ) {
      out.push({
        targetRefid: r.targetRefid,
        cameraBid: r.cameraBid,
        trackSide: r.trackSide,
        decidedAt: r.decidedAt,
        ...extra,
      });
    } else {
      console.warn('[suggestion-decisions] dropped unreadable record');
    }
  }
  return out;
};

/** `layout` carrying these decisions — or without the key when there are none. */
export const withSuggestionDecisions = (
  layout: FloorPlannerLayout,
  decisions: readonly SuggestionDecisionRecord[],
): FloorPlannerLayout => {
  const { suggestionDecisions: _previous, ...rest } = layout;
  return decisions.length > 0 ? { ...rest, suggestionDecisions: [...decisions] } : rest;
};

/** Every field, so a discard's reason changing alone is a change to save (spec R1.8). */
const sameRecord = (d: SuggestionDecisionRecord, e: SuggestionDecisionRecord): boolean =>
  sameKey(d, e) &&
  e.trackSide === d.trackSide &&
  e.decidedAt === d.decidedAt &&
  (e.kind ?? 'keep') === (d.kind ?? 'keep') &&
  e.reason === d.reason &&
  e.size === d.size &&
  e.centroid?.x === d.centroid?.x &&
  e.centroid?.y === d.centroid?.y;

const sameDecisions = (
  a: readonly SuggestionDecisionRecord[],
  b: readonly SuggestionDecisionRecord[],
): boolean => a.length === b.length && a.every(d => b.some(e => sameRecord(d, e)));

/** Whether a save is owed for the decisions alone. */
export const decisionsUnsaved = (): boolean => {
  const { decisions, saved } = useSuggestionDecisionStore.getState();
  return !sameDecisions(decisions, saved);
};
